import { api } from '../../lib/api';
import type { PortalPermission, PortalUser } from '../../types/portalUser';

// CLIENT PORTAL — the STAFF-side API (managing who has client access).
//
// ⚠️ Nothing in this file is used by the portal itself. The portal's own calls
// live in portalApi.ts and go to /api/portal behind a different guard — the two
// are kept apart deliberately so a client-facing component cannot reach a staff
// endpoint by importing the wrong helper.

export async function listPortalUsersForAccount(accountId: string): Promise<PortalUser[]> {
  const res = await api<{ portalUsers: PortalUser[] }>(
    `/api/portal-users?accountId=${encodeURIComponent(accountId)}`,
  );
  return res.portalUsers;
}

export type InvitePortalUserResult = {
  portalUser: PortalUser;
  /**
   * ⚠️ FALSE MEANS "they already had it", NOT a failure. Since scope follows the
   * contact, sharing from a second location the contact is already linked to is
   * a correct request with nothing to do — reporting that as an error is the
   * behaviour this replaced.
   */
  created: boolean;
  /** ⚠️ Reported honestly: the grant is real even when the email did not go. */
  emailSent: boolean;
  emailError: string | null;
};

export async function invitePortalUser(input: {
  email: string;
  displayName: string;
  contactId: string;
  /** ⚠️ The account being shared FROM — the coverage check, never a grant. */
  fromAccountId: string;
  permissions: PortalPermission[];
}): Promise<InvitePortalUserResult> {
  return api<InvitePortalUserResult>('/api/portal-users', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updatePortalUser(
  id: string,
  patch: {
    displayName?: string;
    // ⚠️ No `accountIds` — scope is the contact's account links, and a second
    // way to set it is the drift this design removed.
    permissions?: PortalPermission[];
    status?: 'active' | 'disabled';
  },
): Promise<PortalUser> {
  const res = await api<{ portalUser: PortalUser }>(
    `/api/portal-users/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.portalUser;
}

export async function resendPortalInvite(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/portal-users/${encodeURIComponent(id)}/resend`, {
    method: 'POST',
  });
}

export async function deletePortalUser(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/portal-users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
